/**
 * Statistics over a set of cast votes.
 *
 * Votes carry string values because decks include non-numeric cards ("?",
 * t-shirt sizes); everything here ignores the values that do not parse.
 */

export interface VoteStats {
  /** Mean of the numeric votes, to one decimal place. */
  avg: string;
  min: number;
  max: number;
  /** Total votes cast, including non-numeric ones. */
  count: number;
}

interface VoteLike {
  value?: string;
  /** Older history entries stored the value under `point`. */
  point?: string;
}

/** Numeric vote values, dropping cards like "?" that do not parse. */
export const numericVoteValues = (votes: VoteLike[]): number[] =>
  votes
    .map((vote) => parseFloat(vote.value ?? vote.point ?? ''))
    .filter((value) => !isNaN(value));

/** Mean of the numeric votes, or undefined when none of them parse. */
export const averageVote = (votes: VoteLike[]): number | undefined => {
  const numeric = numericVoteValues(votes);
  if (numeric.length === 0) return undefined;

  return numeric.reduce((sum, value) => sum + value, 0) / numeric.length;
};

/** Full stats for a revealed session, or null when nothing numeric was voted. */
export const calculateVoteStats = (votes: VoteLike[]): VoteStats | null => {
  const numeric = numericVoteValues(votes);
  if (numeric.length === 0) return null;

  const average = numeric.reduce((sum, value) => sum + value, 0) / numeric.length;

  return {
    avg: average.toFixed(1),
    min: Math.min(...numeric),
    max: Math.max(...numeric),
    count: votes.length,
  };
};
